import clsx from 'clsx';

import {
  useAnimatedNumber,
  useSmoothNumber,
} from '../../hooks/useAnimatedNumber';
import { formatUsd, formatNumber, formatUsdSigned } from '../../lib/format';

type Format = 'usd' | 'usd-signed' | 'number';
type Mode = 'tween' | 'smooth';

interface Props {
  value: number | string;
  format?: Format;
  durationMs?: number;
  /**
   * `tween` — ease from previous to new value over `durationMs` (good
   * for one-off updates: balance changes, pnl reveals).
   * `smooth` — continuous low-pass filter (good for high-frequency
   * tickers like the oracle price; new updates merge with in-flight
   * motion instead of restarting the easing curve).
   * Default `tween`.
   */
  mode?: Mode;
  /** Smoothing factor for `mode="smooth"`. 0..1. Default 0.10. */
  alpha?: number;
  className?: string;
  /** Optional element wrapper — defaults to <span>. */
  as?: 'span' | 'div';
}

const FORMATTERS: Record<Format, (n: number) => string> = {
  usd: formatUsd,
  'usd-signed': formatUsdSigned,
  number: formatNumber,
};

export function MotionNumber({
  value,
  format = 'number',
  durationMs,
  mode = 'tween',
  alpha,
  className,
  as: Tag = 'span',
}: Props) {
  const num = typeof value === 'string' ? Number(value) : value;
  const safe = Number.isFinite(num) ? num : 0;
  const tweened = useAnimatedNumber(mode === 'tween' ? safe : 0, { durationMs });
  const smoothed = useSmoothNumber(mode === 'smooth' ? safe : 0, { alpha });
  const display = mode === 'smooth' ? smoothed : tweened;
  const text = FORMATTERS[format](display);
  return (
    <Tag
      className={clsx('pb-numeral tabular-nums', className)}
      data-testid="motion-number"
    >
      {text}
    </Tag>
  );
}
